import { checkErrors, getErrorMessage } from 'app/ui_v2/form-utils';
import { Dayjs } from 'dayjs';
import { useRef, useState } from 'react';
import { useController } from 'react-hook-form';

import { CalendarMonth, Cancel } from '@mui/icons-material';
import { IconButton, InputAdornment, TextField, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

export interface BasicDatePickerProps {
  label: string;
  helperText?: string;
  control: any;
  inputName: string;
  disabled?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  shouldDisableDate?: (date: Dayjs) => boolean;
  onChange?: (date: string | null) => void;
  views?: Array<'year' | 'month' | 'day'>;
  isWithCancel?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
}

export default function BasicDatePicker(props: BasicDatePickerProps) {
  const {
    disabled = false,
    control,
    label,
    inputName,
    helperText = '',
    disablePast = false,
    disableFuture = false,
    shouldDisableDate = () => {
      return false;
    },
    onChange: onCustomChange,
    views = ['year', 'month', 'day'],
    isWithCancel = false,
    minDate,
    maxDate,
  } = props;

  const [isOpened, setIsOpened] = useState<boolean>(false);

  const {
    field: { value, onChange },
    formState: { errors },
  } = useController({
    name: inputName,
    control,
    defaultValue: '',
  });
  const myRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const isErrors = checkErrors(inputName, errors);

  const handleChange = (value: Dayjs | null) => {
    const v = value?.format('YYYY-MM-DD') ?? null;

    onChange(v);
    onCustomChange && onCustomChange(v);

    if (myRef.current !== null) {
      // trigger form onchange§
      myRef.current?.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  return (
    <>
      <div ref={myRef}></div>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={value}
          disabled={disabled}
          open={isOpened}
          onOpen={() => setIsOpened(true)}
          onClose={() => setIsOpened(false)}
          label={label}
          disablePast={disablePast}
          disableFuture={disableFuture}
          disableMaskedInput={true}
          shouldDisableDate={shouldDisableDate}
          views={views}
          minDate={minDate}
          maxDate={maxDate}
          // inputFormat="M/DD/YYYY"
          onChange={(e: Dayjs | null) => {
            handleChange(e);
          }}
          openTo={views[views.length - 1]}
          renderInput={(inputProps) => (
            <TextField
              {...inputProps}
              inputProps={{
                ...inputProps.inputProps,
                readOnly: true,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {value && isWithCancel && (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleChange(null);
                        }}
                        sx={{ mr: -1 }}
                      >
                        <Cancel />
                      </IconButton>
                    )}
                    {(!value || !isWithCancel) && (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsOpened((v) => !v);
                        }}
                        sx={{ mr: -1 }}
                      >
                        <CalendarMonth />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              error={isErrors}
              helperText={getErrorMessage(inputName, errors) ?? helperText ?? null}
              InputLabelProps={{
                shrink: true,
              }}
              onClick={() => setIsOpened(true)}
              sx={{
                '& .MuiInputBase-input': {
                  caretColor: 'transparent',
                  padding: `0 0 0 ${theme.spacing(1)}`,
                  cursor: 'pointer',
                },
              }}
            />
          )}
        />
      </LocalizationProvider>
    </>
  );
}
